import React from 'react';
import { Button, Checkbox, Form, Input, InputNumber, Select, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { TGDDesign, TGDRule, TGDSelector } from '../../../models/courses/graphicDesign';

interface Props { rule: TGDRule; onChange: (rule: TGDRule) => void;
  solution?: TGDDesign; zoneIds?: string[]; versionIds?: string[]; choiceIds?: string[];
  readOnly?: boolean }
const modes: TGDSelector['by'][] = ['text', 'id', 'type', 'icon', 'background', 'role'];
const SelectorField: React.FC<{ value: TGDSelector; onChange: (value: TGDSelector) => void;
  solution?: TGDDesign; readOnly?: boolean }> = ({ value, onChange, solution, readOnly }) => {
  const { t } = useTranslation();
  const componentLabel = (c: TGDDesign['components'][number]) => c.type === 'text'
    ? t('graphicDesignLesson.admin.componentText', { text: c.text || '' })
    : c.type === 'pictogram' ? t('graphicDesignLesson.admin.componentIcon',
      { icon: (c.icon || '').split(':').pop() }) : t('graphicDesignLesson.admin.componentShape',
      { shape: c.sides === 0 ? t('graphicDesignLesson.admin.round')
        : t('graphicDesignLesson.admin.sides', { count: c.sides ?? 4 }) });
  return <div><Space wrap><Select aria-label={t('graphicDesignLesson.admin.selector')}
    value={value.by} disabled={readOnly} style={{ width: 200 }}
    options={modes.map(by => ({ value: by,
      label: t(`graphicDesignLesson.admin.selectorBy.${by}`) }))}
    onChange={by => onChange(by === 'background' ? { by } : { by, value: '' } as TGDSelector)} />
    {value.by === 'id' ? <Select aria-label={t('graphicDesignLesson.admin.value')}
      value={value.value || undefined} disabled={readOnly} style={{ width: 220 }} showSearch
      options={solution?.components.map(c => ({ value: c.id, label: componentLabel(c) })) ?? []}
      onChange={next => onChange({ by: 'id', value: next })} />
      : value.by === 'type' ? <Select value={value.value} disabled={readOnly}
        style={{ width: 180 }} options={['shape', 'pictogram', 'text'].map(item => ({
          value: item, label: t(`graphicDesignLesson.editor.tool.${item}`),
        }))} onChange={next => onChange({ by: 'type', value: next })} />
        : value.by !== 'background' ? <Input aria-label={t('graphicDesignLesson.admin.value')}
          disabled={readOnly} value={value.value} style={{ width: 200 }}
          placeholder={value.by === 'text' ? t('graphicDesignLesson.admin.textExample') : undefined}
          onChange={event => onChange({ ...value, value: event.target.value } as TGDSelector)} />
          : null}</Space>
    {value.by === 'text' && <div><Typography.Text type="secondary">
      {t('graphicDesignLesson.admin.textSelectorHelp')}</Typography.Text></div>}
  </div>;
};
const RuleParamsForm: React.FC<Props> = ({ rule, onChange, solution, zoneIds = [],
  versionIds = [], choiceIds = [], readOnly }) => {
  const { t } = useTranslation();
  const params: any = rule.params;
  const update = (patch: Record<string, unknown>) => onChange({ ...rule,
    params: { ...rule.params, ...patch } } as TGDRule);
  const label = (key: string) => t(`graphicDesignLesson.admin.param.${key}`);
  const valueLabel = () => t(`graphicDesignLesson.admin.valueFor.${rule.type}`);
  const number = (key: string, min = 0, step = 1) => <Form.Item key={key}
    label={key === 'value' ? valueLabel() : label(key)}
    extra={key === 'value' && rule.type === 'contrastMin'
      ? t('graphicDesignLesson.admin.contrastHelp') : undefined}>
    <InputNumber min={min} step={step} disabled={readOnly} value={params[key]}
      onChange={value => update({ [key]: value ?? min })} /></Form.Item>;
  const selector = (key: string) => <Form.Item key={key} label={label(key)}>
    <SelectorField value={params[key] ?? { by: 'background' }} solution={solution}
      readOnly={readOnly}
      onChange={value => update({ [key]: value })} /></Form.Item>;
  const selectors = (key: string) => <Form.Item key={key} label={label(key)}>
    <Space direction="vertical" style={{ width: '100%' }}>
      {(params[key] as TGDSelector[]).map((item, index) => <Space key={index} wrap>
        <SelectorField value={item} solution={solution} readOnly={readOnly}
          onChange={value => update({ [key]: params[key].map((old: TGDSelector, i: number) =>
            i === index ? value : old) })} />
        <Button type="link" disabled={readOnly} onClick={() => update({ [key]:
          params[key].filter((_: unknown, i: number) => i !== index) })}>
          {t('graphicDesignLesson.admin.delete')}</Button></Space>)}
      <Button type="link" disabled={readOnly} onClick={() => update({ [key]:
        [...params[key], { by: 'text', value: '' }] })}>
        {t('graphicDesignLesson.admin.addSelector')}</Button>
    </Space></Form.Item>;
  const pick = (key: string, values: string[], prefix?: string) => <Form.Item key={key}
    label={label(key)}><Select value={params[key] || undefined} disabled={readOnly}
      style={{ width: 240 }} options={values.map(value => ({ value,
        label: prefix ? t(`graphicDesignLesson.admin.${prefix}.${value}`) : value }))}
      onChange={value => update({ [key]: value })} /></Form.Item>;
  const yes = (key: string) => <Form.Item key={key}><Checkbox checked={!!params[key]}
    disabled={readOnly} onChange={event => update({ [key]: event.target.checked })}>
    {label(key)}</Checkbox></Form.Item>;
  let fields: React.ReactNode;
  switch (rule.type) {
    case 'requiredElement': fields = <>{selector('select')}{number('minCount', 1)}</>; break;
    case 'elementCountMax': fields = <>{number('value', 1)}
      {pick('componentType', ['shape', 'pictogram', 'text'], 'componentTypes')}</>; break;
    case 'maxColors': case 'minColors': fields = <>{number('value', 1)}
      {yes('includeBackground')}</>; break;
    case 'contrastMin': fields = <>{selector('a')}{selector('b')}{number('value', 1, 0.1)}</>; break;
    case 'usesPalette': fields = <>{pick('harmony', ['complementary', 'analogous', 'triadic'],
      'harmony')}{number('toleranceDeg')}</>; break;
    case 'sizeRatioMin': fields = <>{selector('a')}{selector('b')}
      {number('value', 1, 0.1)}</>; break;
    case 'hierarchyOrder': fields = <>{selectors('order')}{number('minRatio', 1, 0.05)}</>; break;
    case 'alignedOn': fields = <>{selectors('select')}
      {pick('axis', ['left', 'centerX', 'right', 'top', 'centerY', 'bottom'], 'axis')}
      {number('tolerance')}</>; break;
    case 'timeMax': fields = number('seconds', 1); break;
    case 'zoneFound': fields = pick('zoneId', zoneIds); break;
    case 'choiceEquals': fields = pick('choiceId', versionIds); break;
    case 'reasonEquals': fields = pick('reasonId', choiceIds); break;
    case 'changed': fields = <>{selector('select')}{pick('prop', props, 'prop')}
      {number('minDelta')}</>; break;
    case 'preserved': fields = <>{selector('select')}{pick('prop', props, 'prop')}
      {number('tolerance')}</>; break;
  }
  return <Form layout="vertical">{fields}</Form>;
};
const props = ['x', 'y', 'width', 'height', 'rotation', 'fontSize', 'sides',
  'cornerRadius', 'fill', 'stroke', 'text', 'icon', 'background'];
export default RuleParamsForm;
